import {
  CreateTenantCommand,
  GateOutMismatchMode,
  TenantDto,
  TerminalType,
  UpdateTenantCommand,
} from '@admin/app/api'
import { TenantFormProfile } from './tenant-form.profile'

const mapFormValuesToCreateDto = ({
  name,
  timezone,
  culture,
  address,
  temperatureUnit,
  truckArrivalWindowDuration,
  hasAutoGeneratedCraneSplit,
  skipCraneSplit,
  skipVesselTally,
  skipRailTally,
  skipRailTallyLoadPreparation,
  skipGateOut,
  skipTruckAppointment,
  skipVesselVisitReadyForOperation,
  skipReleaseOrder,
  skipYardPlanning,
  skipBerthTimestamp,
  isInlandJobCreationFlow,
  bayRowUniqueIdentificationMode,
  showUnifiedOperatorPage,
  hasGeneralCargo,
  hasDamageCatalogue,
  unloCodes,
  allowGateInWhenUnitNotOnTerminal,
  allowDirectDelivery,
  allowOperatorToSelectRailcar,
  autoTriggerVesselWorkQueues,
  skipGangSelection,
  gateOutMismatchMode,
  allocationRuleWarningThreshold,
  blockWarningThreshold,
  yardWarningThreshold,
  yardMapAnchorLatitude,
  yardMapAnchorLongitude,
  yardMapNorthRotationDegrees,
}: TenantFormProfile): CreateTenantCommand => ({
  name,
  timezoneId: timezone,
  cultureId: culture,
  address: address,
  temperatureUnit: temperatureUnit,
  truckArrivalWindowDuration: truckArrivalWindowDuration,
  allocationRuleWarningThreshold: allocationRuleWarningThreshold ?? null,
  blockWarningThreshold: blockWarningThreshold ?? null,
  yardWarningThreshold: yardWarningThreshold ?? null,
  hasAutoGeneratedCraneSplit: hasAutoGeneratedCraneSplit ?? false,
  skipCraneSplit: skipCraneSplit ?? false,
  skipVesselTally: skipVesselTally ?? false,
  skipRailTally: skipRailTally ?? false,
  skipRailTallyLoadPreparation: skipRailTallyLoadPreparation ?? false,
  skipGateOut: skipGateOut ?? false,
  skipTruckAppointment: skipTruckAppointment ?? false,
  skipVesselVisitReadyForOperation: skipVesselVisitReadyForOperation ?? false,
  skipReleaseOrder: skipReleaseOrder ?? false,
  skipYardPlanning: skipYardPlanning ?? false,
  skipBerthTimestamp: skipBerthTimestamp ?? false,
  isInlandJobCreationFlow: isInlandJobCreationFlow ?? false,
  bayRowUniqueIdentificationMode: bayRowUniqueIdentificationMode ?? false,
  showUnifiedOperatorPage: showUnifiedOperatorPage ?? false,
  hasGeneralCargo: hasGeneralCargo ?? false,
  hasDamageCatalogue: hasDamageCatalogue ?? false,
  unloCodes: unloCodes ?? [],
  allowGateInWhenUnitNotOnTerminal: allowGateInWhenUnitNotOnTerminal ?? false,
  allowDirectDelivery: allowDirectDelivery ?? false,
  allowOperatorToSelectRailcar: allowOperatorToSelectRailcar ?? false,
  autoTriggerVesselWorkQueues: autoTriggerVesselWorkQueues ?? false,
  skipGangSelection: skipGangSelection ?? false,
  gateOutMismatchMode: gateOutMismatchMode ?? GateOutMismatchMode.Warn,
  yardMapAnchorLatitude: yardMapAnchorLatitude ?? null,
  yardMapAnchorLongitude: yardMapAnchorLongitude ?? null,
  yardMapNorthRotationDegrees: yardMapNorthRotationDegrees ?? null,
})

const mapFormValuesToUpdateDto = ({
  id,
  name,
  timezone,
  culture,
  address,
  temperatureUnit,
  truckArrivalWindowDuration,
  hasAutoGeneratedCraneSplit,
  skipCraneSplit,
  skipVesselTally,
  skipRailTally,
  skipRailTallyLoadPreparation,
  skipGateOut,
  skipTruckAppointment,
  skipVesselVisitReadyForOperation,
  skipReleaseOrder,
  skipYardPlanning,
  skipBerthTimestamp,
  isInlandJobCreationFlow,
  bayRowUniqueIdentificationMode,
  showUnifiedOperatorPage,
  hasGeneralCargo,
  hasDamageCatalogue,
  operatorViewConfigs,
  unloCodes,
  allowGateInWhenUnitNotOnTerminal,
  allowDirectDelivery,
  allowOperatorToSelectRailcar,
  reeferContainerMaxUnpluggedTime,
  autoTriggerVesselWorkQueues,
  skipGangSelection,
  gateOutMismatchMode,
  allocationRuleWarningThreshold,
  blockWarningThreshold,
  yardWarningThreshold,
  yardMapAnchorLatitude,
  yardMapAnchorLongitude,
  yardMapNorthRotationDegrees,
}: TenantFormProfile): UpdateTenantCommand => ({
  id: id!,
  name,
  timezoneId: timezone,
  cultureId: culture,
  address: address,
  temperatureUnit: temperatureUnit,
  truckArrivalWindowDuration: truckArrivalWindowDuration,
  allocationRuleWarningThreshold: allocationRuleWarningThreshold ?? null,
  blockWarningThreshold: blockWarningThreshold ?? null,
  yardWarningThreshold: yardWarningThreshold ?? null,
  hasAutoGeneratedCraneSplit: hasAutoGeneratedCraneSplit ?? false,
  skipCraneSplit: skipCraneSplit ?? false,
  skipVesselTally: skipVesselTally ?? false,
  skipRailTally: skipRailTally ?? false,
  skipRailTallyLoadPreparation: skipRailTallyLoadPreparation ?? false,
  skipGateOut: skipGateOut ?? false,
  skipTruckAppointment: skipTruckAppointment ?? false,
  skipVesselVisitReadyForOperation: skipVesselVisitReadyForOperation ?? false,
  skipReleaseOrder: skipReleaseOrder ?? false,
  skipYardPlanning: skipYardPlanning ?? false,
  skipBerthTimestamp: skipBerthTimestamp ?? false,
  isInlandJobCreationFlow: isInlandJobCreationFlow ?? false,
  bayRowUniqueIdentificationMode: bayRowUniqueIdentificationMode ?? false,
  showUnifiedOperatorPage: showUnifiedOperatorPage ?? false,
  hasGeneralCargo: hasGeneralCargo ?? false,
  hasDamageCatalogue: hasDamageCatalogue ?? false,
  operatorViewConfigs: operatorViewConfigs ?? TerminalType.Hig,
  unloCodes: unloCodes || [],
  allowGateInWhenUnitNotOnTerminal: allowGateInWhenUnitNotOnTerminal ?? false,
  allowDirectDelivery: allowDirectDelivery ?? false,
  allowOperatorToSelectRailcar: allowOperatorToSelectRailcar ?? false,
  reeferContainerMaxUnpluggedTime:
    reeferContainerMaxUnpluggedTime || reeferContainerMaxUnpluggedTime === 0
      ? reeferContainerMaxUnpluggedTime
      : undefined,
  autoTriggerVesselWorkQueues: autoTriggerVesselWorkQueues ?? false,
  skipGangSelection: skipGangSelection ?? false,
  gateOutMismatchMode: gateOutMismatchMode ?? GateOutMismatchMode.Warn,
  yardMapAnchorLatitude: yardMapAnchorLatitude ?? null,
  yardMapAnchorLongitude: yardMapAnchorLongitude ?? null,
  yardMapNorthRotationDegrees: yardMapNorthRotationDegrees ?? null,
})

const mapDtoToFormValues = ({
  id,
  name,
  timezoneId,
  cultureId,
  address,
  temperatureUnit,
  truckArrivalWindowDuration,
  unloCodes,
  configs,
  reeferContainerMaxUnpluggedTime,
}: TenantDto): TenantFormProfile => ({
  id,
  name,
  timezone: timezoneId ?? '',
  culture: cultureId ?? undefined,
  address,
  temperatureUnit: temperatureUnit ?? undefined,
  truckArrivalWindowDuration: truckArrivalWindowDuration ?? null,
  unloCodes,
  hasAutoGeneratedCraneSplit: configs?.hasAutoGeneratedCraneSplit,
  skipCraneSplit: configs?.skipCraneSplit,
  skipVesselTally: configs?.skipVesselTally,
  skipRailTally: configs?.skipRailTally,
  skipRailTallyLoadPreparation: configs?.skipRailTallyLoadPreparation,
  skipGateOut: configs?.skipGateOut,
  skipTruckAppointment: configs?.skipTruckAppointment,
  skipVesselVisitReadyForOperation: configs?.skipVesselVisitReadyForOperation,
  skipReleaseOrder: configs?.skipReleaseOrder,
  skipYardPlanning: configs?.skipYardPlanning,
  skipBerthTimestamp: configs?.skipBerthTimestamp,
  isInlandJobCreationFlow: configs?.isInlandJobCreationFlow,
  bayRowUniqueIdentificationMode: configs?.bayRowUniqueIdentificationMode,
  showUnifiedOperatorPage: configs?.showUnifiedOperatorPage,
  hasGeneralCargo: configs?.hasGeneralCargo,
  hasDamageCatalogue: configs?.hasDamageCatalogue,
  operatorViewConfigs: configs?.selectedOperatorViewConfigs,
  reeferContainerMaxUnpluggedTime: reeferContainerMaxUnpluggedTime,
  allowGateInWhenUnitNotOnTerminal: configs?.allowGateInWhenUnitNotOnTerminal,
  allowDirectDelivery: configs?.allowDirectDelivery,
  allowOperatorToSelectRailcar: configs?.allowOperatorToSelectRailcar,
  autoTriggerVesselWorkQueues: configs?.autoTriggerVesselWorkQueues,
  skipGangSelection: configs?.skipGangSelection,
  gateOutMismatchMode: configs?.gateOutMismatchMode ?? GateOutMismatchMode.Warn,
  allocationRuleWarningThreshold: configs?.allocationRuleWarningThreshold ?? null,
  blockWarningThreshold: configs?.blockWarningThreshold ?? null,
  yardWarningThreshold: configs?.yardWarningThreshold ?? null,
  yardMapAnchorLatitude: configs?.yardMapAnchorLatitude ?? null,
  yardMapAnchorLongitude: configs?.yardMapAnchorLongitude ?? null,
  yardMapNorthRotationDegrees: configs?.yardMapNorthRotationDegrees ?? null,
})

export const TenantFormMapper = {
  mapFormValuesToCreateDto,
  mapFormValuesToUpdateDto,
  mapDtoToFormValues,
}
